import Alert from '@/components/Alert/Alert'
import useHorizontalSafeAreaPadding from '@/lib/hooks/useHorizontalSafeAreaPadding'
import { CoversTagKey } from '@/redux/slices/coverSlice'
import {
  deleteTrashedFormCompletelyThunk,
  fetchTrashedFormsThunk,
  restoreTrashedFormThunk,
  trashedFormSelectors,
} from '@/redux/slices/trashedFormSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useActionSheet } from '@expo/react-native-action-sheet'
import { identity } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, RefreshControl, SectionList } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import CoverListFooter from './CoverListFooter'
import TrashedFormItem from './TrashedFormItem'

const DISMISS_IGNORE_MS = 150

const ACTION_SHEET_OPTIONS = {
  options: ['恢复', '彻底删除', '取消'],
  cancelButtonIndex: 2,
  destructiveButtonIndex: 1,
}

export default function TrashedForms() {
  const dispatch = useAppDispatch()
  const insets = useSafeAreaInsets()
  const horizontalPadding = useHorizontalSafeAreaPadding()
  const { showActionSheetWithOptions } = useActionSheet()

  const trashedFormIds = useAppSelector(state => trashedFormSelectors.selectIds(state) as string[])
  const isLoading = useAppSelector(state => state.trashedForms.isLoading)
  const hasNextPage = useAppSelector(state => state.trashedForms.pageInfo.hasNextPage)

  const [refreshing, setRefreshing] = useState(false)
  const actionSheetOpenRef = useRef(false)
  const actionSheetDismissedAtRef = useRef(0)

  const isEmpty = trashedFormIds.length === 0

  useEffect(() => {
    dispatch(fetchTrashedFormsThunk({ reset: true }))
  }, [dispatch])

  const sections = useMemo(() => {
    if (trashedFormIds.length > 0) {
      return [{ title: '回收站', data: trashedFormIds }]
    }
    return []
  }, [trashedFormIds])

  const handleItemPress = useCallback(
    (token: string) => {
      if (Date.now() - actionSheetDismissedAtRef.current < DISMISS_IGNORE_MS) {
        return
      }
      if (actionSheetOpenRef.current) {
        actionSheetOpenRef.current = false
        return
      }

      actionSheetOpenRef.current = true

      showActionSheetWithOptions(
        {
          ...ACTION_SHEET_OPTIONS,
          containerStyle: Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined,
        },
        buttonIndex => {
          actionSheetOpenRef.current = false
          actionSheetDismissedAtRef.current = Date.now()
          if (buttonIndex === 0) {
            dispatch(restoreTrashedFormThunk(token))
          } else if (buttonIndex === 1) {
            Alert.alert('彻底删除后将无法恢复，确认删除？', undefined, [
              { text: '取消', style: 'cancel' },
              { text: '删除', style: 'destructive', onPress: () => dispatch(deleteTrashedFormCompletelyThunk(token)) },
            ])
          }
        }
      )
    },
    [dispatch, showActionSheetWithOptions, insets.bottom]
  )

  const renderItem = useCallback(
    ({ item: token, index }: { item: string; index: number }) => (
      <TrashedFormItem
        token={token}
        showBorder={index < trashedFormIds.length - 1}
        onPress={() => handleItemPress(token)}
      />
    ),
    [trashedFormIds.length, handleItemPress]
  )

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    dispatch(fetchTrashedFormsThunk({ reset: true }))
    setRefreshing(false)
  }, [dispatch])

  const handleLoadMore = useCallback(() => {
    if (isLoading || isEmpty) return
    if (!hasNextPage) return

    dispatch(fetchTrashedFormsThunk({ reset: false }))
  }, [isLoading, isEmpty, hasNextPage, dispatch])

  return (
    <SectionList
      sections={sections}
      renderItem={renderItem}
      stickySectionHeadersEnabled={false}
      keyExtractor={identity}
      className="bg-background-0"
      style={{ flex: 1 }}
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ ...horizontalPadding, paddingTop: 16, paddingBottom: insets.bottom + 16 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      onEndReached={handleLoadMore}
      onEndReachedThreshold={0.1}
      ListFooterComponent={() => <CoverListFooter empty={isEmpty} loading={isLoading} context={CoversTagKey.Trash} />}
    />
  )
}
